// <wholesale-order-form>: live totals, remembered quantities and a single "add all to cart" for the colour x size order
// form (bag and box sections are stacked in one view) (see snippets/wholesale-order-form.liquid, which renders the grids server-side).
//
// The subtotal is an ESTIMATE built from the prices the server rendered (wholesale tier applied for display);
// Shopify's automatic discounts decide what is actually charged, in the cart and at checkout.
//
// Quantities are remembered in sessionStorage, keyed by variant id, so moving to another colour's page (every sibling
// page renders the same grids, so the variant ids match) doesn't lose what was entered.
//
// Stock: for a variant Shopify tracks and doesn't oversell, each cell is capped at stock MINUS what is already in the cart
// (the cart comes from /cart.js on load, after every add and on every cart change). Cells with items in the cart show
// "カート内 N"; a cell with nothing left is disabled and says "カート上限".
//
// Adding never relies on a response to say what happened: the cart is read before and after the attempt and the difference
// is what was added. One bulk /cart/add.js first; a 4xx means nothing was added and the lines are retried one at a time,
// sequentially. A 429 is retried with exponential backoff (Retry-After honoured) and never fired line by line.
if (!window.customElements.get('wholesale-order-form')) {
  const STORAGE_KEY = 'wholesale-order-form:v1';
  const MAX_THROTTLE_RETRIES = 3;
  const LINE_SPACING_MS = 250;

  const readStore = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writeStore = (store) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
      // storage unavailable (private mode, quota): the form still works, it just won't remember
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // { variantId: quantity } from a /cart.js payload (lines of the same variant are summed)
  const quantitiesOf = (cart) => {
    const map = {};
    (cart?.items || []).forEach((item) => {
      map[item.variant_id] = (map[item.variant_id] || 0) + item.quantity;
    });
    return map;
  };

  class WholesaleOrderForm extends HTMLElement {
    connectedCallback() {
      // Not entitled to order this brand (guest / pending / other brand): no inputs were rendered, only the access panel, which is
      // laid over the size columns of every section. Nothing else to set up.
      if (this.dataset.locked) {
        this.setupLockedPanel();
        return;
      }

      this.unitsElement = this.querySelector('[data-wof-units]');
      this.subtotalElement = this.querySelector('[data-wof-subtotal]');
      this.breakdownElement = this.querySelector('[data-wof-breakdown]');
      this.messageElement = this.querySelector('[data-wof-message]');
      this.summaryElement = this.querySelector('[data-wof-summary]');
      this.failuresElement = this.querySelector('[data-wof-failures]');
      this.addedElement = this.querySelector('[data-wof-added]');
      this.submitButton = this.querySelector('.wholesale-order-form__submit');

      // the quantity rule's own max, kept apart from the max the script computes from stock and the cart
      this.querySelectorAll('.wholesale-order-form__input').forEach((input) => {
        input.dataset.ruleMax = input.getAttribute('max') ?? '';
      });

      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', this.onInput.bind(this));
      this.addEventListener('focusin', (event) => {
        if (event.target.matches('.wholesale-order-form__input')) event.target.select();
      });
      this.submitButton?.addEventListener('click', this.onSubmit.bind(this));

      this.onCartChange = (event) => this.refreshLimits(event.detail?.cart);
      this.onPageShow = () => this.refreshLimits();
      document.addEventListener('cart:change', this.onCartChange);
      window.addEventListener('pageshow', this.onPageShow);

      this.setupSticky();
      this.restore();
      this.recalculate();
      this.refreshLimits();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this.onCartChange);
      window.removeEventListener('pageshow', this.onPageShow);
      this.stickyObserver?.disconnect();
      this.lockedObserver?.disconnect();
    }

    // ---- access panel (not entitled) ------------------------------------------------------------------------------
    // One panel from the first grid's first row to the bottom of the last grid, from the right edge of the widest colour column to the
    // right edge. Section headings, price lines and size header rows stay above it (CSS). When the grids are shorter than the panel's
    // content, the area grows so nothing overlaps what follows. Without this script the panel simply follows the grids.
    setupLockedPanel() {
      const wrapper = this.querySelector('.wholesale-order-form__sections');
      const panel = this.querySelector('[data-wof-locked-panel]');
      if (!wrapper || !panel) return;

      const place = () => {
        const tables = [...wrapper.querySelectorAll('.wholesale-order-form__table')];
        if (tables.length === 0) return;

        wrapper.style.removeProperty('padding-block-end');
        const box = wrapper.getBoundingClientRect();
        const firstBody = (tables[0].tBodies[0] || tables[0]).getBoundingClientRect();
        const lastBottom = tables[tables.length - 1].getBoundingClientRect().bottom;
        const left = Math.max(...tables.map((table) => table.querySelector('.wholesale-order-form__corner')?.getBoundingClientRect().right ?? box.left));
        const content = panel.firstElementChild;
        const available = lastBottom - firstBody.top;
        const needed = content ? content.getBoundingClientRect().height : 0;

        wrapper.style.setProperty('--wof-locked-top', `${firstBody.top - box.top}px`);
        wrapper.style.setProperty('--wof-locked-left', `${left - box.left}px`);
        wrapper.style.setProperty('--wof-locked-height', `${Math.max(available, needed)}px`);
        if (needed > available) wrapper.style.paddingBlockEnd = `${needed - available}px`;
        wrapper.classList.add('is-locked-placed');
      };

      place();
      if ('ResizeObserver' in window) {
        this.lockedObserver = new ResizeObserver(() => window.requestAnimationFrame(place));
        this.lockedObserver.observe(this);
      }
    }

    // ---- sticky summary on phones (layout only: its button presses the summary's own button) ----------------------

    // Under 768px a compact bar (合計 N点, subtotal, add button) is fixed to the bottom while a grid is on screen and something has
    // been entered. While a phone has quantities, room for the bar is reserved under the last grid (stable: it does not change while
    // scrolling), so the last row can always be scrolled clear of the bar.
    setupSticky() {
      this.stickyElement = this.querySelector('[data-wof-sticky]');
      if (!this.stickyElement || !('IntersectionObserver' in window)) return;

      this.stickyUnitsElement = this.stickyElement.querySelector('[data-wof-sticky-units]');
      this.stickySubtotalElement = this.stickyElement.querySelector('[data-wof-sticky-subtotal]');
      this.stickyElement.querySelector('[data-wof-sticky-submit]')?.addEventListener('click', () => this.submitButton?.click());

      this.stickyMedia = window.matchMedia('(max-width: 767px)');
      this.stickyMedia.addEventListener('change', () => this.updateSticky());

      this.gridsInView = new Set();
      this.stickyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.gridsInView.add(entry.target);
          else this.gridsInView.delete(entry.target);
        });
        this.updateSticky();
      });
      this.querySelectorAll('.wholesale-order-form__table').forEach((table) => this.stickyObserver.observe(table));
    }

    updateSticky() {
      if (!this.stickyElement || !this.stickyMedia) return;

      const phone = this.stickyMedia.matches;
      const hasUnits = (this.currentUnits || 0) > 0;
      const show = phone && hasUnits && this.gridsInView.size > 0;

      this.classList.toggle('has-sticky-space', phone && hasUnits);
      this.stickyElement.hidden = !show;
      if (show) this.style.setProperty('--wof-sticky-height', `${this.stickyElement.offsetHeight}px`);
    }

    get inputs() {
      return Array.from(this.querySelectorAll('.wholesale-order-form__input:not(:disabled)'));
    }

    // Whole, non-negative number, capped at the cell's max (quantity rule, or stock minus what is in the cart).
    quantityOf(input) {
      let quantity = parseInt(input.value, 10);
      if (!Number.isFinite(quantity) || quantity < 0) quantity = 0;

      const max = parseInt(input.getAttribute('max'), 10);
      if (Number.isFinite(max) && quantity > max) quantity = max;

      return quantity;
    }

    // ---- cart ----------------------------------------------------------------------------------------------------

    async fetchCart() {
      const response = await fetch(`${Shopify.routes.root}cart.js?_=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`cart.js ${response.status}`);
      return response.json();
    }

    // Cell max = min(quantity rule max, stock - in cart); a marker on cells that already have items in the cart.
    async refreshLimits(cart) {
      try {
        cart = cart?.items ? cart : await this.fetchCart();
      } catch (error) {
        return; // keep whatever limits the cells have; Shopify still validates the add
      }

      const inCart = quantitiesOf(cart);

      this.querySelectorAll('.wholesale-order-form__input').forEach((input) => {
        const field = input.closest('.wholesale-order-form__field');
        const marker = field?.querySelector('[data-wof-in-cart]');
        const count = inCart[input.dataset.variantId] || 0;
        const ruleMax = parseInt(input.dataset.ruleMax, 10);
        const stock = input.dataset.stock === undefined ? NaN : parseInt(input.dataset.stock, 10);

        let cap = Number.isFinite(ruleMax) ? ruleMax : Infinity;
        if (Number.isFinite(stock)) cap = Math.min(cap, Math.max(0, stock - count));

        const exhausted = Number.isFinite(stock) && cap <= 0;

        input.disabled = exhausted;
        field?.classList.toggle('is-capped', exhausted);

        // The label the input was rendered with, so it can be restored (an exhausted cell reads 上限 inside the box, no extra line).
        input.dataset.label ??= input.getAttribute('aria-label') || '';
        input.placeholder = exhausted ? this.dataset.limitShort : '0';
        input.setAttribute('aria-label', exhausted ? `${input.dataset.label} - ${this.dataset.cartLimit}` : input.dataset.label);

        if (exhausted) {
          input.value = '';
          input.removeAttribute('max');
        } else if (Number.isFinite(cap)) {
          input.max = cap;
          // a cell that just failed keeps what was typed (it stays red until the buyer edits it)
          if (!input.classList.contains('is-invalid') && parseInt(input.value, 10) > cap) input.value = cap;
        } else {
          input.removeAttribute('max');
        }

        // "カート内 N": a small overlay (cart mark + number) at the cell's top-left. It takes no space, so no cell changes height.
        if (marker) {
          const label = this.dataset.inCart.replace('__COUNT__', count);
          marker.hidden = count === 0;
          marker.querySelector('[data-wof-in-cart-count]').textContent = count;
          marker.title = label;
          marker.setAttribute('aria-label', label);
        }
      });

      // The legend line under each grid: shown when it has a low-stock badge or a cell with items in the cart.
      this.querySelectorAll('[data-wof-legend]').forEach((legend) => {
        const grid = legend.previousElementSibling;
        legend.hidden = !grid?.querySelector('.wholesale-order-form__stock-badge, [data-wof-in-cart]:not([hidden])');
      });

      this.recalculate();
    }

    // ---- remembering quantities ----------------------------------------------------------------------------------

    restore() {
      const store = readStore();

      this.inputs.forEach((input) => {
        const saved = parseInt(store.quantities?.[input.dataset.variantId], 10);
        if (Number.isFinite(saved) && saved > 0) input.value = saved;
      });
    }

    persist() {
      const store = readStore();
      const quantities = {};

      this.inputs.forEach((input) => {
        const quantity = this.quantityOf(input);
        if (quantity > 0) quantities[input.dataset.variantId] = quantity;
      });

      store.quantities = quantities;
      writeStore(store);
    }

    clearStored() {
      const store = readStore();
      delete store.quantities;
      writeStore(store);
    }

    // ---- totals --------------------------------------------------------------------------------------------------

    onInput(event) {
      const input = event.target;
      if (!input.matches('.wholesale-order-form__input')) return;

      // Keep the field honest as the buyer types (whole number, within the cell's max), without fighting them while it is empty.
      const quantity = this.quantityOf(input);
      if (input.value !== '' && String(quantity) !== input.value) input.value = quantity;

      // Any edit clears the whole error panel and every red cell (and the "added" panel): nothing stale is left behind.
      this.clearMessages();

      this.recalculate();
      this.persist();
    }

    recalculate() {
      const unitsByPackaging = {};
      let units = 0;
      let subtotalMinor = 0;

      this.inputs.forEach((input) => {
        const quantity = this.quantityOf(input);
        const packaging = input.dataset.packaging;

        unitsByPackaging[packaging] = (unitsByPackaging[packaging] || 0) + quantity;
        units += quantity;
        subtotalMinor += quantity * (parseInt(input.dataset.price, 10) || 0);
      });

      if (this.unitsElement) this.unitsElement.textContent = units.toString();
      if (this.subtotalElement) this.subtotalElement.textContent = this.formatMoney(subtotalMinor);

      // the phone sticky bar mirrors the summary
      this.currentUnits = units;
      if (this.stickyUnitsElement) this.stickyUnitsElement.textContent = this.stickyUnitsElement.dataset.template.replace('__COUNT__', units);
      if (this.stickySubtotalElement) this.stickySubtotalElement.textContent = this.formatMoney(subtotalMinor);
      this.updateSticky();

      // Units entered in each packaging section's heading.
      this.querySelectorAll('[data-wof-section-count]').forEach((count) => {
        const sectionUnits = unitsByPackaging[count.dataset.wofSectionCount] || 0;
        count.textContent = sectionUnits > 0 ? `${sectionUnits}${this.dataset.unitsSuffix}` : '';
        count.hidden = sectionUnits === 0;
      });

      // "ボックス入り 6点 / バイオバッグ入り 12点" in section order - only when both packagings have something in them.
      if (this.breakdownElement) {
        const parts = [...this.querySelectorAll('[data-wof-section]')]
          .map((section) => section.dataset.wofSection)
          .filter((packaging) => unitsByPackaging[packaging] > 0)
          .map((packaging) => `${this.dataset[`label${packaging === 'box' ? 'Box' : 'Bag'}`]} ${unitsByPackaging[packaging]}${this.dataset.unitsSuffix}`);

        this.breakdownElement.textContent = parts.join(' / ');
        this.breakdownElement.hidden = parts.length < 2;
      }
    }

    formatMoney(minorUnits) {
      const currency = this.dataset.currency || 'JPY';

      try {
        // Liquid prices are always the amount x 100, including for zero-decimal currencies such as JPY.
        return new Intl.NumberFormat(document.documentElement.lang || 'ja', { style: 'currency', currency }).format(minorUnits / 100);
      } catch (error) {
        return (minorUnits / 100).toString();
      }
    }

    // ---- messages ------------------------------------------------------------------------------------------------

    showMessage(text, tone) {
      if (!this.messageElement) return;

      this.summaryElement.textContent = text;
      this.failuresElement.replaceChildren();
      this.messageElement.classList.toggle('text-error', tone === 'error');
      this.messageElement.hidden = false;

      // The summary sits at the top of the form: make sure an error is actually in view when it appears.
      if (tone === 'error') {
        const rect = this.messageElement.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          this.messageElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }

    // Start of every submit and any edit: error panel, red cells and the "added" panel all go.
    clearMessages() {
      if (this.messageElement) {
        this.messageElement.hidden = true;
        this.summaryElement.textContent = '';
        this.failuresElement.replaceChildren();
      }

      if (this.addedElement) {
        this.addedElement.hidden = true;
        this.addedElement.querySelector('[data-wof-added-list]')?.replaceChildren();
      }

      this.querySelectorAll('.wholesale-order-form__input.is-invalid').forEach((input) => {
        input.classList.remove('is-invalid');
        input.removeAttribute('aria-invalid');
      });
    }

    // "ボックス入り / Black / M" (the packaging is only named when the form has both sections)
    lineName(input) {
      const hasBothSections = this.querySelectorAll('[data-wof-section]').length > 1;
      const packaging = hasBothSections ? this.dataset[`label${input.dataset.packaging === 'box' ? 'Box' : 'Bag'}`] : '';

      return [packaging, input.dataset.colorName, input.dataset.sizeName].filter(Boolean).join(' / ');
    }

    showFailures(summaryText, failures) {
      this.showMessage(summaryText, 'error');

      failures.forEach(({ input, reason }) => {
        const item = document.createElement('li');
        item.dataset.variantId = input.dataset.variantId;

        // each line is a button that takes the buyer to that cell
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wholesale-order-form__failure-link';
        button.textContent = `${this.lineName(input)}: ${reason}`;
        button.addEventListener('click', () => {
          input.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
          input.focus({ preventScroll: true });
        });

        item.append(button);
        this.failuresElement.append(item);

        input.classList.add('is-invalid');
        input.setAttribute('aria-invalid', 'true');
      });

      // Error-summary pattern: scroll to the summary and focus it (it is announced as an alert); the red cells are below it.
      this.messageElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
      this.messageElement.focus({ preventScroll: true });
    }

    // What was added, listed under the error summary after a partial add, with a link to the cart.
    showAdded(results) {
      if (!this.addedElement) return;

      const list = this.addedElement.querySelector('[data-wof-added-list]');
      this.addedElement.querySelector('[data-wof-added-title]').textContent = this.dataset.addedTitle;
      list.replaceChildren(...results.map(({ input, added }) => {
        const item = document.createElement('li');
        item.textContent = this.dataset.addedLine.replace('__LINE__', this.lineName(input)).replace('__COUNT__', added);
        return item;
      }));

      this.addedElement.hidden = false;
    }

    // ---- add to cart ---------------------------------------------------------------------------------------------

    collectLines() {
      // grid order: the viewed product's packaging first, then the other, rows top to bottom, sizes left to right (DOM order)
      return this.inputs
        .map((input) => ({ input, id: Number(input.dataset.variantId), quantity: this.quantityOf(input) }))
        .filter((line) => line.quantity > 0);
    }

    // One POST. A 429 is retried with exponential backoff, honouring Retry-After; everything else is returned as is.
    async post(payload) {
      for (let attempt = 0; ; attempt += 1) {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });

        let body = null;
        try {
          body = await response.json();
        } catch (error) {
          body = null;
        }

        if (response.status !== 429 || attempt >= MAX_THROTTLE_RETRIES) return { response, body };

        const retryAfter = parseFloat(response.headers.get('Retry-After'));
        await sleep(Math.min(Number.isFinite(retryAfter) ? retryAfter * 1000 : 1000 * 2 ** attempt, 8000));
      }
    }

    // The attempt itself. It reports reasons for failed lines, but what was really added is decided by the cart diff.
    async attempt(lines) {
      const outcome = { reasons: new Map(), throttled: false, unknown: false, status: null };

      let bulk = null;
      try {
        bulk = await this.post({ items: lines.map(({ id, quantity }) => ({ id, quantity })) });
      } catch (error) {
        console.error('wholesale-order-form: bulk add request failed', error);
        outcome.unknown = true;
        return outcome;
      }

      if (bulk.response.ok) return outcome;

      outcome.status = bulk.response.status;
      console.warn('wholesale-order-form: bulk add not accepted', bulk.response.status, bulk.body);

      if (bulk.response.status === 429) {
        outcome.throttled = true;
        return outcome;
      }

      if (bulk.response.status >= 500) {
        outcome.unknown = true;
        outcome.detail = bulk.body?.description || bulk.body?.message || '';
        return outcome;
      }

      // A 4xx: Shopify added nothing, so the lines can be retried one at a time (sequentially, spaced out).
      for (const line of lines) {
        await sleep(LINE_SPACING_MS);

        let result = null;
        try {
          result = await this.post({ id: line.id, quantity: line.quantity });
        } catch (error) {
          outcome.unknown = true;
          break;
        }

        if (result.response.ok) continue;

        if (result.response.status === 429) {
          outcome.throttled = true;
          break;
        }

        if (result.response.status >= 500) {
          outcome.unknown = true;
          break;
        }

        outcome.reasons.set(line.id, result.body?.description || result.body?.message || this.dataset.failureUnknown);
      }

      return outcome;
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.submitButton.getAttribute('aria-busy') === 'true') return;

      const lines = this.collectLines();
      if (lines.length === 0) {
        this.clearMessages();
        this.showMessage(this.dataset.enterQuantityMessage, 'error');
        return;
      }

      this.clearMessages();
      this.submitButton.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        await this.submitLines(lines);
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.submitButton.removeAttribute('aria-busy');
      }
    }

    async submitLines(lines) {
      // Snapshot the cart before, so what was added is the difference to the cart afterwards, whatever the responses said.
      let before = null;
      try {
        before = quantitiesOf(await this.fetchCart());
      } catch (error) {
        this.showMessage(this.dataset.errorGeneric, 'error');
        return;
      }

      // Same flow as the theme's own <product-form>, so the cart drawer / cart count refresh the native way.
      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      const outcome = await this.attempt(lines);

      let afterCart = null;
      try {
        afterCart = await this.fetchCart();
      } catch (error) {
        await sleep(600);
        try {
          afterCart = await this.fetchCart();
        } catch (retryError) {
          afterCart = null;
        }
      }

      // Cannot verify the cart: keep everything and say so rather than guess.
      if (!afterCart) {
        this.showMessage(this.dataset.errorGeneric, 'error');
        return;
      }

      const after = quantitiesOf(afterCart);
      const results = lines.map((line) => ({ ...line, added: Math.max(0, (after[line.id] || 0) - (before[line.id] || 0)) }));
      const done = results.filter((result) => result.added >= result.quantity);
      const partial = results.filter((result) => result.added > 0 && result.added < result.quantity);
      const failed = results.filter((result) => result.added < result.quantity);
      const addedLines = results.filter((result) => result.added > 0);
      const addedUnits = addedLines.reduce((total, result) => total + result.added, 0);

      // Accepted cells are cleared (a partly accepted one keeps only the remainder).
      done.forEach(({ input }) => { input.value = ''; });
      partial.forEach(({ input, quantity, added }) => { input.value = quantity - added; });

      // Cart UI, cell limits and totals from the cart we already have (after the failed cells are marked, so they keep their value).
      const finalize = async () => {
        if (addedLines.length > 0) await this.refreshCart(afterCart, sectionsToBundle, { open: failed.length === 0 });
        await this.refreshLimits(afterCart);
        this.recalculate();
        this.persist();
      };

      if (failed.length === 0) {
        this.clearStored();
        await finalize();
        this.finishSuccess(addedUnits);
        return;
      }

      // the HTTP status is for support only (console): a buyer sees plain Japanese, never "(HTTP 422)"
      if (outcome.status) console.warn('wholesale-order-form: add finished with HTTP', outcome.status);

      // Nothing added because Shopify is throttling: keep every quantity, no red cells, one calm message.
      if (addedLines.length === 0 && outcome.throttled) {
        this.showMessage(this.dataset.throttled, 'error');
        await finalize();
        return;
      }

      // Nothing added and the outcome is unknown (network / 5xx): keep everything, show the reason Shopify gave.
      if (addedLines.length === 0 && outcome.unknown && outcome.reasons.size === 0) {
        this.showMessage([outcome.detail, this.dataset.errorGeneric].filter(Boolean).join(' '), 'error');
        await finalize();
        return;
      }

      const summary = addedLines.length === 0
        ? this.dataset.summaryNone
        : this.dataset.summaryPartial.replace('__SUCCESS__', addedLines.length).replace('__FAILED__', failed.length);

      const fallbackReason = outcome.throttled ? this.dataset.throttled : this.dataset.failureUnknown;

      this.showFailures(summary, failed.map(({ input, id, added }) => ({
        input,
        reason: outcome.reasons.get(id) || (added > 0 ? this.dataset.stockReason : fallbackReason)
      })));

      if (addedLines.length > 0) this.showAdded(addedLines);

      await finalize();
    }

    // Brings the cart drawer / count up to date from the cart we already fetched. Per-line responses carry no rendered
    // section HTML, so the bundled sections (the drawer) are fetched once from the Section Rendering API.
    // A partial add must not pop the drawer over the red cells, so it uses a baseEvent the drawer doesn't open on.
    async refreshCart(cart, sectionsToBundle, { open = false } = {}) {
      const settings = window.themeVariables?.settings || {};

      try {
        if (sectionsToBundle.size > 0) {
          cart.sections = await (await fetch(`${Shopify.routes.root}?sections=${[...sectionsToBundle].join(',')}`)).json();
        }

        if (open) {
          if (settings.cartType === 'page') {
            window.location.href = `${Shopify.routes.root}cart`;
            return;
          }

          this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: cart.items, cart } }));
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: open ? 'variant:add' : 'wholesale-order-form:partial', cart } }));
      } catch (error) {
        // fall back to the theme's own "reload the cart from the server" event
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }

    finishSuccess(unitCount) {
      const settings = window.themeVariables?.settings || {};
      const addedMessage = (this.dataset.addedMessage || '').replace('__COUNT__', unitCount);

      if (settings.cartType === 'message') {
        document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: addedMessage } }));
      } else {
        this.showMessage(addedMessage, 'success');

        const statusHolder = document.getElementById('status-announcement');
        if (statusHolder) {
          statusHolder.textContent = '';
          setTimeout(() => { statusHolder.textContent = addedMessage; }, 100);
        }
      }
    }
  }

  window.customElements.define('wholesale-order-form', WholesaleOrderForm);
}
